<template>
  <LiveStormEventCard
    v-if="closestEvent"
    :is-live="isLive"
    :event="closestEvent"
    @click="handleJoinClickWithEvent(closestEvent)"
  />
</template>

<script lang="ts" setup>
import { onMounted, ref, computed, onUnmounted } from "vue";
import { getLiveStormEvents } from "vue-app/api/LiveStormService";
import { LiveStormEventInfo, LiveStormEventType, LiveStormSession } from "vue-app/components/LiveStormEvents/LiveStormEvents.types";
import { dayjsWithTz } from "vue-app/utils/functions";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
import LiveStormEventCard from "./LiveStormEventCard.vue";

const closestEvent = ref<LiveStormEventInfo | null>(null);
const currentTimeInSeconds = ref(dayjsWithTz().unix());
let intervalId: ReturnType<typeof setInterval> | undefined;

const isLive = computed(() => {
  if (!closestEvent.value) {
    return false;
  }
  const eventStartTime = closestEvent.value.time;
  const eventEndTime = eventStartTime + closestEvent.value.duration * 60;
  return currentTimeInSeconds.value >= eventStartTime && currentTimeInSeconds.value < eventEndTime;
});

const handleJoinClickWithEvent = (event: LiveStormEventInfo | null) => {
  if (event) {
    amplitudeSendEvent("LiveStorm_Webinar_Clicked", {
      eventTitle: event.title,
      isLive: isLive.value,
      eventLanguage: event.language,
    });

    const email = window.EDRONE_CONTEXT?.user?.email || "";
    const registrationLink = event.registration_link + "?email=" + email;
    window.open(registrationLink, "_blank");
  }
};

const shouldShowEvent = (event: LiveStormEventInfo) => {
  const isPaying = window.EDRONE_CONTEXT.tracker.isPaying;
  const description = event.description?.toLowerCase() || "";

  if (isPaying) {
    return description.includes("#inspiration");
  }

  return description.includes("#freemium") || description.includes("#activation");
};

const parseEventTypeFromDescription = (description: string): LiveStormEventType => {
  const descriptionLower = description?.toLowerCase() || "";

  if (descriptionLower.includes("#training")) {
    return "training";
  }
  if (descriptionLower.includes("#workshop")) {
    return "workshop";
  }
  return "webinar";
};

const sortEventsByLiveAndTime = (a: LiveStormEventInfo, b: LiveStormEventInfo, nowSeconds: number) => {
  const aIsLive = a.time <= nowSeconds;
  const bIsLive = b.time <= nowSeconds;

  if (aIsLive && !bIsLive) {
    return -1;
  }
  if (!aIsLive && bIsLive) {
    return 1;
  }

  return a.time - b.time;
};

const findClosestEvent = (events: LiveStormEventInfo[]) => {
  const userLang = window.EDRONE_CONTEXT.user.lang.toLowerCase();

  const nowSeconds = dayjsWithTz().unix();
  const ongoingOrUpcomingEvents = events
    .filter((event) => {
      const eventEndTime = event.time + event.duration * 60;
      return eventEndTime > nowSeconds && event.language.toLowerCase().includes(userLang);
    })
    .filter(shouldShowEvent)
    .sort((a, b) => sortEventsByLiveAndTime(a, b, nowSeconds));
  if (ongoingOrUpcomingEvents.length > 0) {
    closestEvent.value = ongoingOrUpcomingEvents[0];
  } else {
    closestEvent.value = null;
  }
};

function buildSessionsMap(includedData: LiveStormSession[]): Map<string, LiveStormSession> {
  const sessionsMap = new Map<string, LiveStormSession>();
  for (const item of includedData) {
    if (item.type === "sessions") {
      sessionsMap.set(item.id, item);
    }
  }
  return sessionsMap;
}

async function loadAndProcessEvents() {
  const response = await getLiveStormEvents();
  const events: LiveStormEventInfo[] = [];

  const eventData = response.data;
  const includedData = response.included;

  let sessionsMap = new Map();
  if (includedData) {
    sessionsMap = buildSessionsMap(includedData);
  }

  for (const event of eventData) {
    if (event.relationships && event.relationships.sessions) {
      for (const sessionRef of event.relationships.sessions.data) {
        const session = sessionsMap.get(sessionRef.id);
        if (session) {
          const timeArray = session.attributes.estimated_started_at;
          const timestamp = dayjs
            .utc()
            .year(timeArray[0])
            .month(timeArray[1] - 1)
            .date(timeArray[2])
            .hour(timeArray[3])
            .minute(timeArray[4])
            .second(0)
            .unix();

          const description = event.attributes.description || "";

          events.push({
            time: timestamp,
            title: event.attributes.title,
            description: description,
            registration_link: event.attributes.registration_link,
            duration: event.attributes.estimated_duration,
            language: event.attributes.language,
            eventType: parseEventTypeFromDescription(description),
          });
        }
      }
    }
  }
  findClosestEvent(events);
  intervalId = setInterval(() => {
    currentTimeInSeconds.value = dayjsWithTz().unix();
    findClosestEvent(events);
  }, 1000);
}

onMounted(async () => {
  await loadAndProcessEvents();
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>
